/**
 * Minimal node-gettext implementation
 * https://github.com/alexanderwallin/node-gettext/blob/master/lib/gettext.js
 * Security measures against Prototype Pollution (node-gettext CVE):
 * https://security.snyk.io/vuln/SNYK-JS-NODEGETTEXT-6100943
 * 1. Use Map instead of Object to prevent prototype pollution
 * 2. Input validation on all methods
 * 3. No recursive merge functions used
 * 4. Use Object.create(null) for fallback objects
 * 5. Schema validation of translation data
 *
 * Audited: 2026-01-29
 */
import { LANG } from '../constants';

// Default gettext domain name
const DEFAULT_DOMAIN = 'messages';

class MinimalGettext {
  constructor() {
    // Security: Use Map instead of Object to prevent prototype pollution
    // Structure: catalogs[locale][domain] = translations object
    this.catalogs = new Map();
    this.locale = 'de';
  }

  /**
   * Stores a set of translations in the set of node-gettext
   * catalogs.
   *
   * @example
   *     gt.addTranslations('en', 'messages', translationsObject)
   *
   * @param {String} locale        A locale string
   * @param {String} domain        A domain name
   * @param {Object} translations  An object of gettext-parser JSON shape
   */
  addTranslations(locale, domain, translations) {
    // Security: Validate input schema
    if (typeof locale !== 'string' || typeof domain !== 'string') {
      console.warn(
        'addTranslations() requires locale and domain to be strings',
      );
      return;
    }

    if (!translations || typeof translations !== 'object') {
      console.warn('addTranslations() requires valid translations object');
      return;
    }

    // Security: Use Map to prevent prototype pollution
    if (!this.catalogs.has(locale)) {
      this.catalogs.set(locale, new Map());
    }

    // Store the entire translations object for this locale/domain
    // Expected format: { translations: { "msgctxt": { "msgid": { msgid, msgstr } } } }
    this.catalogs.get(locale).set(domain, translations);
  }

  /**
   * Sets the locale to get translated messages for.
   *
   * @example
   *     gt.setLocale('sv-SE')
   *
   * @param {String} locale  A locale
   */
  setLocale(locale) {
    // Input validation
    if (typeof locale !== 'string') {
      console.warn(
        `You called setLocale() with an argument of type ${typeof locale}. The locale must be a string.`,
      );
      return;
    }

    if (locale.trim() === '') {
      console.warn(
        'You called setLocale() with an empty value, which makes little sense.',
      );
      return;
    }

    // Warn if translations for this locale don't exist (except for default 'de')
    if (locale !== 'de' && !this.catalogs.has(locale)) {
      console.warn(
        `You called setLocale() with "${locale}", but no translations for that locale have been added.`,
      );
    }

    this.locale = locale;
  }

  /**
   * Retrieves translation object from the domain and context
   *
   * @private
   * @param  {String} domain   A gettext domain name
   * @param  {String} msgctxt  Translation context
   * @param  {String} msgid    String to be translated
   * @return {Object} Translation object or null if not found
   */
  #getTranslationEntry(domain, msgctxt, msgid) {
    const catalog = this.catalogs.get(this.locale)?.get(domain);
    if (!catalog || !catalog.translations) return null;

    const context = msgctxt || '';
    const contextMessages = catalog.translations[context];
    if (!contextMessages) return null;

    return contextMessages[msgid] || null;
  }

  /**
   * Translates a plural string from a specific context using a specific textdomain
   *
   * @example
   *     gt.dnpgettext('domainname', 'sports', 'Back', '%d backs', numberOfBacks)
   *
   * @param  {String} domain       A gettext domain name
   * @param  {String} msgctxt      Translation context
   * @param  {String} msgid        String to be translated
   * @param  {String} msgidPlural  If no translation was found, return this on count!=1
   * @param  {Number} count        Number count for the plural
   * @return {String} Translation or the original string if no translation was found
   */
  #dnpgettext(domain, msgctxt, msgid, msgidPlural, count) {
    let defaultTranslation = msgid;
    let index;

    const context = msgctxt || '';

    // Determine default translation based on count
    if (!Number.isNaN(count) && count !== 1) {
      defaultTranslation = msgidPlural || msgid;
    }

    const translation = this.#getTranslationEntry(domain, context, msgid);

    if (translation) {
      if (typeof count === 'number') {
        // Simple plural form handling (German/English: n != 1)
        // For more languages, would need proper pluralsFunc
        index = count === 1 ? 0 : 1;
      } else {
        index = 0;
      }

      return translation.msgstr[index] || defaultTranslation;
    }

    // Warn if translation missing for non-default locale
    if (this.locale !== 'de') {
      console.warn(
        `No translation was found for msgid "${msgid}" in msgctxt "${context}" and domain "${domain}"`,
      );
    }

    return defaultTranslation;
  }

  /**
   * Translates a string using the default textdomain
   *
   * @example
   *     gt.gettext('Some text')
   *
   * @param  {String} msgid  String to be translated
   * @return {String} Translation or the original string if no translation was found
   */
  gettext(msgid) {
    if (typeof msgid !== 'string') return '';
    return this.#dnpgettext(DEFAULT_DOMAIN, '', msgid, null, undefined);
  }

  /**
   * Translates a plural string using the default textdomain
   *
   * @example
   *     gt.ngettext('One thing', 'Many things', numberOfThings)
   *
   * @param  {String} msgid        String to be translated when count is not plural
   * @param  {String} msgidPlural  String to be translated when count is plural
   * @param  {Number} count        Number count for the plural
   * @return {String} Translation or the original string if no translation was found
   */
  ngettext(msgid, msgidPlural, n) {
    if (typeof msgid !== 'string' || typeof msgidPlural !== 'string') return '';
    if (typeof n !== 'number') return msgid;
    return this.#dnpgettext(DEFAULT_DOMAIN, '', msgid, msgidPlural, n);
  }

  /**
   * Translates a string from a specific context using the default textdomain
   *
   * @example
   *    gt.pgettext('sports', 'Back')
   *
   * @param  {String} msgctxt  Translation context
   * @param  {String} msgid    String to be translated
   * @return {String} Translation or the original string if no translation was found
   */
  pgettext(context, msgid) {
    if (typeof context !== 'string' || typeof msgid !== 'string') return '';
    return this.#dnpgettext(DEFAULT_DOMAIN, context, msgid, null, undefined);
  }

  /**
   * Translates a plural string from a specific context using the default textdomain
   *
   * @example
   *     gt.npgettext('sports', 'Back', '%d backs', numberOfBacks)
   *
   * @param  {String} msgctxt      Translation context
   * @param  {String} msgid        String to be translated when count is not plural
   * @param  {String} msgidPlural  String to be translated when count is plural
   * @param  {Number} count        Number count for the plural
   * @return {String} Translation or the original string if no translation was found
   */
  npgettext(msgctxt, msgid, msgidPlural, count) {
    if (
      typeof msgctxt !== 'string' ||
      typeof msgid !== 'string' ||
      typeof msgidPlural !== 'string'
    )
      return '';
    if (typeof count !== 'number') return msgid;
    return this.#dnpgettext(DEFAULT_DOMAIN, msgctxt, msgid, msgidPlural, count);
  }
}

const gt = new MinimalGettext();

// Register translations from window global (set by po2js.js output)
if (window.translations && typeof window.translations === 'object') {
  Object.keys(window.translations).forEach((lang) => {
    gt.addTranslations(lang, DEFAULT_DOMAIN, window.translations[lang]);
  });
}

// Set current locale, if not German
if (LANG !== 'de') {
  gt.setLocale(LANG);
}

export default gt;
